import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { AlertTriangle, FileText, Users, Briefcase, Mail, Settings, ArrowRight, CheckCircle, XCircle, UserPlus } from 'lucide-react';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    pendingApplications: 0,
    activeAdvisors: 0,
    businessOwners: 0,
    emailsSentThisMonth: 0
  });
  const [subscriptionBreakdown, setSubscriptionBreakdown] = useState({
    beta: 0,
    comped: 0,
    paid: 0
  });
  const [advisorsByCategory, setAdvisorsByCategory] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [betaMode, setBetaMode] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch('/api/admin/dashboard', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats || stats);
        setSubscriptionBreakdown(data.subscriptionBreakdown || subscriptionBreakdown);
        setAdvisorsByCategory(data.advisorsByCategory || []);
        setRecentActivity(data.recentActivity || []);
        setBetaMode(data.betaMode !== false);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getActivityIcon = (action) => {
    switch (action) {
      case 'application_submitted': return <FileText className="w-4 h-4 text-primary" />;
      case 'advisor_approved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'advisor_rejected': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'user_signup': return <UserPlus className="w-4 h-4 text-primary" />;
      case 'email_sent': return <Mail className="w-4 h-4 text-amber-500" />;
      default: return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <AdminLayout activeTab="dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="dashboard">
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>

        {betaMode && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">BETA MODE ACTIVE</p>
                <p className="text-sm text-amber-700">All new advisors receive free Beta subscriptions.</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/settings')}
              className="flex items-center space-x-1 text-amber-700 hover:text-amber-900 text-sm font-medium"
            >
              <Settings className="w-4 h-4" />
              <span>Settings</span>
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div 
            onClick={() => navigate('/admin/applications?status=new')}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 cursor-pointer hover:shadow-md hover:border-primary/20 transition-all"
          >
            <div className="text-3xl font-bold text-gray-900">{stats.pendingApplications}</div>
            <div className="text-sm text-gray-500 mt-1">New Applications</div>
            <button className="mt-3 text-sm text-primary hover:text-primary-dark flex items-center space-x-1">
              <span>Review</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div 
            onClick={() => navigate('/admin/advisors?status=active')}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 cursor-pointer hover:shadow-md hover:border-primary/20 transition-all"
          >
            <div className="text-3xl font-bold text-gray-900">{stats.activeAdvisors}</div>
            <div className="text-sm text-gray-500 mt-1">Active Advisors</div>
          </div>

          <div 
            onClick={() => navigate('/admin/business-owners')}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 cursor-pointer hover:shadow-md hover:border-primary/20 transition-all"
          >
            <div className="text-3xl font-bold text-gray-900">{stats.businessOwners}</div>
            <div className="text-sm text-gray-500 mt-1">Business Owners</div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="text-3xl font-bold text-gray-900">{stats.emailsSentThisMonth}</div>
            <div className="text-sm text-gray-500 mt-1">Emails Sent This Month</div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription Overview</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Beta (Free)</span>
              <div className="flex items-center space-x-3">
                <div className="w-48 bg-gray-100 rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full" 
                    style={{ width: `${Math.min((subscriptionBreakdown.beta / (subscriptionBreakdown.beta + subscriptionBreakdown.comped + subscriptionBreakdown.paid || 1)) * 100, 100)}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-gray-900 w-8">{subscriptionBreakdown.beta}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Comped</span>
              <div className="flex items-center space-x-3">
                <div className="w-48 bg-gray-100 rounded-full h-2">
                  <div 
                    className="bg-amber-500 h-2 rounded-full" 
                    style={{ width: `${Math.min((subscriptionBreakdown.comped / (subscriptionBreakdown.beta + subscriptionBreakdown.comped + subscriptionBreakdown.paid || 1)) * 100, 100)}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-gray-900 w-8">{subscriptionBreakdown.comped}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Paid</span>
              <div className="flex items-center space-x-3">
                <div className="w-48 bg-gray-100 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full" 
                    style={{ width: `${Math.min((subscriptionBreakdown.paid / (subscriptionBreakdown.beta + subscriptionBreakdown.comped + subscriptionBreakdown.paid || 1)) * 100, 100)}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-gray-900 w-8">{subscriptionBreakdown.paid}</span>
              </div>
            </div>
          </div>
        </div>

        {advisorsByCategory.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Advisors by Category</h2>
            <div className="flex flex-wrap gap-4">
              {advisorsByCategory.map((cat) => (
                <div key={cat.slug} className="bg-bg-secondary px-3 py-2 rounded-lg text-sm">
                  <span className="font-medium text-gray-900">{cat.name}:</span>
                  <span className="text-primary ml-1 font-semibold">{cat.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
            <button 
              onClick={() => navigate('/admin/settings?tab=activity')}
              className="text-sm text-primary hover:text-primary-dark flex items-center space-x-1"
            >
              <span>View All</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-3">
            {recentActivity.length === 0 ? (
              <p className="text-sm text-gray-500">No recent activity</p>
            ) : (
              recentActivity.slice(0, 5).map((activity, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="flex items-center space-x-3">
                    {getActivityIcon(activity.action)}
                    <span className="text-sm text-gray-700">{activity.description}</span>
                  </div>
                  <span className="text-xs text-gray-500">{formatTimeAgo(activity.created_at)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
